<template>
  <div class="search-input">
    <div class="search-input__wrapper">
      <SearchIcon class="search-input__magnifying-glass-icon" size="24" />
      <input v-model="searchQuery" :placeholder="placeholder" />
      <XIcon
        v-show="searchQuery"
        class="search-input__close-icon"
        size="16"
        @click="searchQuery = ''"
      />
    </div>
    <div v-if="sortingDropdown" class="search-input__sorting-options">
      <Dropdown
        :options="sortingOptions"
        :selectedOption="sortingOptions[sortingSelected]"
        :handleSelection="handleSorting"
      />
    </div>
    <div v-if="viewToggler" class="search-input__toggle-view">
      <Toggles :viewStyle="viewStyle" @update-view-style="handleViewStyle" />
    </div>
    <div v-if="resultsLabel" class="search-input__results">
      {{ searchLabel }}
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import { SearchIcon, XIcon } from 'vue-feather-icons'
import Dropdown from '@/components/forms/elements/Dropdown.vue'
import Toggles from '@/components/shared/Toggles.vue'

export default {
  name: 'SearchInput',
  components: {
    SearchIcon,
    XIcon,
    Dropdown,
    Toggles,
  },
  props: {
    placeholder: {
      type: String,
      required: false,
      default: 'Search...',
    },
    sortingDropdown: {
      type: Boolean,
      required: false,
      default: false,
    },
    sortingOptions: {
      type: Array,
      required: false,
      default() {
        return []
      },
    },
    sortingSelected: {
      type: Number,
      required: false,
      default: 0,
    },
    viewToggler: {
      type: Boolean,
      required: false,
      default: false,
    },
    filterLocally: {
      type: Boolean,
      required: false,
      default: true,
    },
    resultsLabel: {
      type: Boolean,
      required: false,
      default: false,
    },
    searchIndex: {
      type: Object,
      required: false,
      default: null,
    },
  },
  data() {
    return {
      totalResults: '10000',
      viewStyle: 'tile',
    }
  },
  computed: {
    ...mapState({
      stateQuery: state => state.search.searchQuery,
    }),
    searchLabel() {
      const searchQueryLabel = this.searchQuery
        ? ` for “${this.searchQuery}”`
        : ''
      return `${this.totalResults} results ${searchQueryLabel}`
    },
    sortingSelectedOption() {
      return this.sortingOptions[this.sortingSelected]
    },
    searchQuery: {
      get() {
        return this.stateQuery
      },
      set(query) {
        this.setQuery(query)
      },
    },
  },
  methods: {
    ...mapActions({
      setQuery: 'search/setQuery',
    }),
    handleSorting(sortingOption) {
      this.$emit('handle-sorting', sortingOption)
    },
    handleViewStyle(style) {
      this.viewStyle = style
      this.$emit('handle-view-style', style)
    },
  },
  mounted() {
    if (!this.filterLocally) this.setQuery('')
  },
}
</script>

<style lang="scss" scoped>
.search-input {
  display: flex;
  flex-wrap: wrap;
  column-gap: 20px;
  row-gap: 10px;

  &__wrapper {
    display: flex;
    align-items: center;
    flex: 1;
    padding: 0 10px;
    gap: 5px;
    border-radius: 4px;
    background: var(--white-90);
    &:hover {
      background: var(--white);
    }
    &:focus-within {
      box-shadow: var(--purple-dark-box-shadow);
      background: var(--white);

      svg {
        color: var(--butterfly-bush);
      }
    }
    &__x-icon {
      cursor: pointer;
    }
    svg {
      color: var(--dove-gray);
    }
    input {
      flex: 1;
      background: transparent;
      border: none;
      height: 45px;
      font-family: var(--font-book);
      font-style: normal;
      font-weight: 450;
      font-size: rem(12px);
      line-height: rem(14px);
    }
  }
  &__close-icon {
    cursor: pointer;
  }
  &__results {
    width: 100%;
    color: var(--white);
    font-family: var(--font-medium);
    font-style: normal;
    font-weight: 500;
    font-size: rem(18px);
    line-height: rem(24px);
    margin-top: 10px;

    @include mobile {
      margin-top: 20px;
      font-family: var(--font-bold);
      font-weight: 700;
      font-size: rem(24px);
      line-height: rem(32px);
    }
    @include desktop {
      text-align: left;
    }
  }
  &__sorting-options {
    min-width: 220px;
    width: 100%;
    @include mobile {
      width: inherit;
    }
  }
  &__toggle-view {
    align-self: center;
  }
}
</style>
